import React from 'react';
import {
  Dialog, Box, Stack, Typography, Button, IconButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useUploadBlocker, UPLOAD_BLOCKER_FEATURES } from './uploadBlockerShared';
import artImage from '../../Assets/frnds.png';

// New-UI (v2) presentation of the upload-blocker popup. Split card: art panel +
// details, matching the v2 design language (Montserrat, #15171c ink, dark CTA).
const FONT = 'Montserrat, sans-serif';

function UploadBlockerPopupV2({ onHide, variant = 'default' }) {
  const { priceText, goToPlans, copy } = useUploadBlocker(onHide, variant);

  return (
    <Dialog
      open
      onClose={onHide}
      PaperProps={{
        sx: {
          borderRadius: 3, width: '100%', maxWidth: 720, m: 2, overflow: 'hidden',
        },
      }}
    >
      <Box sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        fontFamily: FONT,
      }}
      >
        <IconButton
          onClick={onHide}
          size="small"
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <CloseRoundedIcon fontSize="small" sx={{ color: '#15171c' }} />
        </IconButton>

        {/* Art panel */}
        <Box sx={{
          flex: { xs: 'none', sm: '0 0 42%' },
          minHeight: { xs: 150, sm: 380 },
          p: { xs: '20px', sm: '26px 24px' },
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          textAlign: 'left',
          backgroundImage: `linear-gradient(150deg, rgba(21,23,28,0.72), rgba(21,23,28,0.42)), url(${artImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        >
          <Box sx={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            bgcolor: 'rgba(255,255,255,0.2)',
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
          }}
          >
            <StarRoundedIcon sx={{ fontSize: 15 }} />
            {copy.badgeShort}
          </Box>
          <Box>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, fontSize: 24, lineHeight: 1.2, textShadow: '0 2px 14px rgba(0,0,0,0.35)', textAlign: 'left',
            }}
            >
              Your very own
              <br />
              streaming app
            </Typography>
            <Typography sx={{
              fontFamily: FONT, fontSize: 13, mt: 1.25, opacity: 0.92, lineHeight: 1.5, textAlign: 'left',
            }}
            >
              Keep every memory in one place — upload, stream and share without limits.
            </Typography>
          </Box>
        </Box>

        {/* Details panel */}
        <Box sx={{
          flex: 1, p: { xs: '24px 22px', sm: '34px 30px 30px' }, display: 'flex', flexDirection: 'column',
        }}
        >
          <Typography sx={{
            fontFamily: FONT, fontSize: 12, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#7b61ff', mb: 1,
          }}
          >
            {copy.eyebrow}
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontWeight: 800, fontSize: 22, color: '#15171c', mb: 0.75, lineHeight: 1.25,
          }}
          >
            {copy.title}
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontSize: 14, color: '#5b6472', mb: 2.25, lineHeight: 1.5,
          }}
          >
            {copy.subtitle}
          </Typography>

          {copy.showPrice && priceText && (
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 2.25 }}>
              <Typography sx={{
                fontFamily: FONT, fontWeight: 800, fontSize: 34, color: '#15171c', lineHeight: 1,
              }}
              >
                {priceText}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#6b7280', fontWeight: 500 }}>/ year after trial</Typography>
            </Stack>
          )}

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {UPLOAD_BLOCKER_FEATURES.map((f) => (
              <Stack key={f} direction="row" alignItems="center" spacing={1.25}>
                <Box sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: '#e8f7ee',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 22px',
                }}
                >
                  <CheckRoundedIcon sx={{ fontSize: 15, color: '#1aa251' }} />
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#2b303b' }}>{f}</Typography>
              </Stack>
            ))}
          </Stack>

          <Stack spacing={1.25} sx={{ mt: 'auto', alignItems: 'center' }}>
            <Button
              fullWidth
              disableElevation
              variant="contained"
              onClick={goToPlans}
              sx={{
                bgcolor: '#15171c',
                color: '#fff',
                textTransform: 'none',
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 2,
                py: 1.4,
                '&:hover': { bgcolor: '#000' },
              }}
            >
              {copy.primaryCta}
            </Button>
            <Button
              onClick={onHide}
              sx={{
                textTransform: 'none', fontFamily: FONT, fontSize: 13, fontWeight: 500, color: '#6b7280', '&:hover': { bgcolor: 'transparent', color: '#15171c' },
              }}
            >
              {copy.secondaryCta}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}

export default UploadBlockerPopupV2;
